import { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  subscribeInfringementIndex,
  subscribeTripOfflineInfringements,
  subscribeTripSpeedingInfringements,
} from "../services/firestoreTracking.service";

const DEFAULT_FILTERS = {
  driver: "",
  vehicle: "",
  trip: "",
  type: "all",
  status: "all",
  confidence: "all",
  originId: "all",
  dateRange: null,
};

function containsValue(haystack, needle) {
  return (haystack || "").toString().toLowerCase().includes((needle || "").toString().trim().toLowerCase());
}

function matchesConfidence(record, confidence) {
  if (!confidence || confidence === "all") return true;
  const value = Number(record?.confidenceValue);
  if (!Number.isFinite(value)) return confidence === "low";
  if (confidence === "high") return value >= 0.9;
  if (confidence === "medium") return value >= 0.75 && value < 0.9;
  if (confidence === "low") return value < 0.75;
  return true;
}

function matchesDateRange(record, dateRange) {
  if (!Array.isArray(dateRange) || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) {
    return true;
  }

  const eventAtMs = Number(record?.eventAtMs);
  if (!Number.isFinite(eventAtMs)) return false;

  const startMs = dayjs(dateRange[0]).startOf("day").valueOf();
  const endMs = dayjs(dateRange[1]).endOf("day").valueOf();
  return eventAtMs >= startMs && eventAtMs <= endMs;
}

function applyFilters(records, filters, tripId) {
  const activeFilters = { ...DEFAULT_FILTERS, ...(filters || {}) };
  const activeOriginIds = Array.isArray(activeFilters.originIds) ? activeFilters.originIds : [];

  return (records || []).filter((record) => {
    if (tripId && String(record?.tripOrderId) !== String(tripId)) return false;
    if (activeFilters.type !== "all" && activeFilters.type !== record?.type) return false;
    if (activeFilters.status !== "all" && activeFilters.status !== (record?.reviewStatus || "pending")) return false;
    if (activeFilters.originId !== "all" && activeFilters.originId !== record?.originId) return false;
    if (activeFilters.originId === "all" && activeOriginIds.length && !activeOriginIds.includes(record?.originId)) return false;
    if (!matchesConfidence(record, activeFilters.confidence)) return false;
    if (!matchesDateRange(record, activeFilters.dateRange)) return false;
    if (activeFilters.driver && !containsValue(record?.driverName || record?.driverIdentifier, activeFilters.driver)) return false;
    if (activeFilters.vehicle && !containsValue(`${record?.vehicleRegistration || ""} ${record?.vehicleLabel || ""}`, activeFilters.vehicle)) return false;
    if (activeFilters.trip && !containsValue(`${record?.tripReference || ""} ${record?.tripOrderId || ""}`, activeFilters.trip)) return false;
    return true;
  });
}

function mergeRecords(indexRecords = [], tripRecords = []) {
  const byId = new Map();

  for (const record of indexRecords || []) {
    if (!record?.id) continue;
    byId.set(record.id, record);
  }

  for (const record of tripRecords || []) {
    if (!record?.id) continue;
    const previous = byId.get(record.id) || {};
    byId.set(record.id, {
      ...previous,
      ...record,
      type: record.type || previous.type || "speeding",
      reviewStatus:
        previous.reviewStatus && record.reviewStatus === "pending"
          ? previous.reviewStatus
          : record.reviewStatus || previous.reviewStatus || "pending",
      reviewNote: previous.reviewNote || record.reviewNote || "",
      latestActionType: previous.latestActionType || record.latestActionType || null,
      actionStatus: previous.actionStatus || record.actionStatus || null,
    });
  }

  return [...byId.values()].sort(
    (a, b) => Number(b?.eventAtMs || 0) - Number(a?.eventAtMs || 0)
  );
}

export function useInfringementStream({
  tripId = null,
  filters = {},
  limitCount = 250,
  enabled = true,
  includeTripSubcollections = false,
} = {}) {
  const [rawRecords, setRawRecords] = useState([]);
  const [tripSpeedingRecords, setTripSpeedingRecords] = useState([]);
  const [tripOfflineRecords, setTripOfflineRecords] = useState([]);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);

  useEffect(() => {
    const queryOriginId = filters?.originId && filters.originId !== "all" ? filters.originId : null;
    const queryOriginIds = !queryOriginId && Array.isArray(filters?.originIds) ? filters.originIds : [];

    if (!enabled) {
      setRawRecords([]);
      setTripSpeedingRecords([]);
      setTripOfflineRecords([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeInfringementIndex(
      { tripId, limitCount, originId: queryOriginId, originIds: queryOriginIds },
      (records) => {
        setRawRecords(records);
        setLoading(false);
      },
      (nextError) => {
        setError(nextError);
        setLoading(false);
      }
    );

    const unsubscribeTripSpeeding =
      includeTripSubcollections && tripId
        ? subscribeTripSpeedingInfringements(
            { tripId, limitCount },
            (records) => setTripSpeedingRecords(records),
            (nextError) => {
              setError(nextError);
              setLoading(false);
            }
          )
        : null;

    const unsubscribeTripOffline =
      includeTripSubcollections && tripId
        ? subscribeTripOfflineInfringements(
            { tripId, limitCount },
            (records) => setTripOfflineRecords(records),
            (nextError) => {
              setError(nextError);
              setLoading(false);
            }
          )
        : null;

    return () => {
      unsubscribe?.();
      unsubscribeTripSpeeding?.();
      unsubscribeTripOffline?.();
    };
  }, [tripId, limitCount, enabled, includeTripSubcollections, filters?.originId, filters?.originIds]);

  const mergedRawRecords = mergeRecords(
    rawRecords,
    includeTripSubcollections ? [...tripSpeedingRecords, ...tripOfflineRecords] : []
  );
  const records = applyFilters(mergedRawRecords, filters, tripId);

  return {
    records,
    rawRecords: mergedRawRecords,
    loading,
    error,
  };
}

export { DEFAULT_FILTERS as infringementDefaultFilters };
